/* eslint-disable no-param-reassign */
import type { TFunction } from 'i18next';

import { createMenuManagementAxiosInstance } from 'createAxiosInstance';
import { MIDNIGHT } from 'constants/menuManagement';
import { REQUEST_TYPE } from 'constants/api';
import { axiosApiRequest } from 'helpers/api';

// Loose shape for the raw, deeply-nested Cuboh menu API response. Fields
// beyond the ones this file reads are preserved via the index signature.
type RawCubohNode = {
  uuid: string;
  parentUuidId: string;
  ordinal?: number;
  [key: string]: unknown;
};

type RawCubohModifierGroup = RawCubohNode & {
  cubohModifier?: RawCubohModifier[];
};

type RawCubohModifier = RawCubohNode & {
  cubohModifierGroup?: RawCubohModifierGroup[];
};

type RawCubohItem = RawCubohNode & {
  cubohModifierGroup?: RawCubohModifierGroup[];
};

type RawCubohCategory = RawCubohNode & {
  cubohMainItem?: RawCubohItem[];
};

export type RawCubohMenu = {
  cubohCategory?: RawCubohCategory[];
  [key: string]: unknown;
};

// Flattened dict entries: the same raw node, plus a `key` unique identifier
// computed from uuid+parent. Modifier groups also get `groupKey` — a
// duplicate of `key` that survives being spread onto a JSX element (React
// strips a literal `key` prop from what a child component can read).
type FlatEntry<T> = T & { key: string };
export type CategoryEntry = FlatEntry<RawCubohCategory>;
export type ItemEntry = FlatEntry<RawCubohItem>;
export type ModifierGroupEntry = FlatEntry<RawCubohModifierGroup> & { groupKey: string };
export type ModifierEntry = FlatEntry<RawCubohModifier>;

export type PreviewMenu = Omit<RawCubohMenu, 'cubohCategory'> & {
  categories: Record<string, CategoryEntry>;
  items: Record<string, ItemEntry>;
  modifierGroups: Record<string, ModifierGroupEntry>;
  modifiers: Record<string, ModifierEntry>;
};

export const sortByItemTypeOrdinal =
  (data: Record<string, Record<string, { ordinal: number }>>, itemType: string) =>
  (a: string, b: string) =>
    data[itemType][a].ordinal - data[itemType][b].ordinal;

export const formatCachedMenu = (menu: RawCubohMenu): PreviewMenu => {
  const categories = (menu.cubohCategory ?? []).reduce<Record<string, CategoryEntry>>(
    (accumulator, category) => {
      const uniqueId = `${category.uuid}_${category.parentUuidId}`;
      accumulator[uniqueId] = {
        ...category,
        key: uniqueId,
      };
      return accumulator;
    },
    {},
  );

  const items = Object.values(categories)
    .filter(category => category.cubohMainItem)
    .reduce<Record<string, ItemEntry>>((accumulator, currentCategory) => {
      (currentCategory.cubohMainItem ?? []).forEach(item => {
        const uniqueId = `${item.uuid}_${item.parentUuidId}`;
        accumulator[uniqueId] = {
          key: uniqueId,
          ...item,
        };
      });
      return accumulator;
    }, {});

  const modifierGroups: Record<string, ModifierGroupEntry> = {};
  const modifiers: Record<string, ModifierEntry> = {};

  // Nested modifier groups live on a modifier's own `cubohModifierGroup` field
  // (e.g. picking "Side Salad" reveals its own "Choose your dressing" group),
  // mirroring an item's top-level modifier groups. Walk both mutually
  // recursively so any depth of nesting lands in the same flat dicts.
  function walkModifierGroups(groupList?: RawCubohModifierGroup[]): void {
    (groupList ?? []).forEach(modifierGroup => {
      const uniqueId = `${modifierGroup?.uuid}_${modifierGroup?.parentUuidId}`;
      modifierGroups[uniqueId] = {
        key: uniqueId,
        // `key` is stripped by React when a group is spread onto a JSX
        // element (`{...modifierGroup}`), so `groupKey` carries the same
        // unique id through as a real, readable prop.
        groupKey: uniqueId,
        ...modifierGroup,
      };
      // eslint-disable-next-line no-use-before-define -- mutual recursion with walkModifiers
      walkModifiers(modifierGroup.cubohModifier);
    });
  }

  function walkModifiers(modifierList?: RawCubohModifier[]): void {
    (modifierList ?? []).forEach(modifier => {
      const uniqueId = `${modifier.uuid}_${modifier.parentUuidId}`;
      modifiers[uniqueId] = {
        key: uniqueId,
        ...modifier,
      };
      if (modifier.cubohModifierGroup) {
        walkModifierGroups(modifier.cubohModifierGroup);
      }
    });
  }

  Object.values(items)
    .filter(item => item.cubohModifierGroup)
    .forEach(item => walkModifierGroups(item.cubohModifierGroup));

  delete menu.cubohCategory;
  Object.values(categories).forEach(category => {
    delete category.cubohMainItem;
  });
  Object.values(items).forEach(item => {
    delete item.cubohModifierGroup;
  });
  Object.values(modifierGroups).forEach(modifierGroup => {
    delete modifierGroup.cubohModifier;
  });
  Object.values(modifiers).forEach(modifier => {
    delete modifier.cubohModifierGroup;
  });

  return {
    ...menu,
    categories,
    items,
    modifierGroups,
    modifiers,
  };
};

const snakeToCamel = (str: string): string => {
  const splitStringArr = str.split('_');
  return splitStringArr.reduce((acc, curr, i) => {
    const part = i !== 0 ? curr[0].toUpperCase() + curr.slice(1) : curr;
    return acc + part;
  }, '');
};

const convertResponse = (response: Record<string, unknown>): Record<string, unknown> => {
  const parentKeys = Object.keys(response);
  parentKeys.forEach(key => {
    const currentObj = response[key];
    delete response[key];
    const newKey = snakeToCamel(key);
    response[newKey] = currentObj;
    if (response[newKey] && typeof response[newKey] === 'object') {
      convertResponse(response[newKey] as Record<string, unknown>);
    }
  });
  return response;
};

type LoadMenuArgs = {
  companyID: string | number;
  merchantID: string | number;
  menuID: string;
  setError?: (...args: unknown[]) => void;
};

export const loadMenu = async ({
  companyID,
  merchantID,
  menuID,
  setError,
}: LoadMenuArgs): Promise<PreviewMenu> => {
  const data = await axiosApiRequest({
    createAxiosInstance: createMenuManagementAxiosInstance,
    method: REQUEST_TYPE.GET,
    path: `company/${companyID}/cuboh/merchant/${merchantID}/menus/detailed/full/${menuID}`,
    data: undefined,
    setError,
  });
  const { cubohMenu } = data;
  const [cachedMenu] = cubohMenu;
  return formatCachedMenu(convertResponse(cachedMenu));
};

// The 3 shapes a modifier group's form value can take, depending on whether
// it renders as checkboxes, a radio group, or quantity inputs.
export type CheckboxGroupValue = Array<{ key: string; price?: number }>;
export type RadioGroupValue = {
  uuid: string;
  parentUuidId: string;
  key: string;
  price?: number;
  [key: string]: unknown;
};
export type InputGroupValue = Record<string, { quantity: number; price?: number }>;
export type ModifierGroupFormValue = CheckboxGroupValue | RadioGroupValue | InputGroupValue;

export const isCheckboxValue = (itemValue: unknown): itemValue is CheckboxGroupValue =>
  Array.isArray(itemValue);

export const isRadioGroupValue = (itemValue: unknown): itemValue is RadioGroupValue => {
  const { parentUuidId, uuid, key } = (itemValue ?? {}) as Partial<RadioGroupValue>;
  return `${uuid}_${parentUuidId}` === key;
};

export const isInputGroupValue = (itemValue: unknown): itemValue is InputGroupValue =>
  !isCheckboxValue(itemValue) && !isRadioGroupValue(itemValue);

// Whether `modifierKey` is currently selected within a modifier group's form
// value, regardless of which of the 3 group-type shapes that value holds.
export const isModifierSelected = ({
  groupValue,
  modifierKey,
}: {
  groupValue: ModifierGroupFormValue | undefined;
  modifierKey: string;
}): boolean => {
  if (!groupValue) return false;
  if (isCheckboxValue(groupValue)) {
    return groupValue.some(selected => selected.key === modifierKey);
  }
  if (isRadioGroupValue(groupValue)) {
    return groupValue.key === modifierKey;
  }
  if (isInputGroupValue(groupValue)) {
    return (groupValue[modifierKey]?.quantity ?? 0) > 0;
  }
  return false;
};

export const isModifierGroupRequirementMet = ({
  group,
  groupValue,
}: {
  group: PreviewModifierGroup;
  groupValue: ModifierGroupFormValue | undefined;
}): boolean => {
  const isRequired = (group.minimum ?? 0) > 0;
  if (!isRequired) return true;

  if (group.maximum === 1) {
    return !!groupValue;
  }
  if (isCheckboxValue(groupValue)) {
    return groupValue.length >= (group.minimum ?? 0);
  }
  const totalQuantity = Object.values((groupValue as InputGroupValue) || {}).reduce(
    (acc, mod) => acc + (mod?.quantity ?? 0),
    0,
  );
  return totalQuantity >= (group.minimum ?? 0);
};

export const deselectModifier = ({
  groupValue,
  modifierKey,
}: {
  groupValue: ModifierGroupFormValue | undefined;
  modifierKey: string;
}): ModifierGroupFormValue | undefined => {
  if (!groupValue) return groupValue;
  if (isCheckboxValue(groupValue)) {
    return groupValue.filter(selected => selected.key !== modifierKey);
  }
  if (isRadioGroupValue(groupValue)) {
    return groupValue.key === modifierKey ? undefined : groupValue;
  }
  if (isInputGroupValue(groupValue)) {
    return { ...groupValue, [modifierKey]: { ...groupValue[modifierKey], quantity: 0 } };
  }
  return groupValue;
};

// The leaner, presentation-specific shapes `buildVisibleModifierGroups`
// returns for rendering — distinct from the raw flat-dict entries above.
export type PreviewModifier = {
  name?: string;
  description?: string;
  price?: number;
  uuid?: string;
  parentUuidId?: string;
  key: string;
  paused?: boolean;
  // UI-only decorations, populated by MenuManagementPreviewItemModal (never
  // by buildVisibleModifierGroups itself) so the drill-down indicator knows
  // whether this modifier has its own nested group and, if selected, a
  // summary of what was chosen within it.
  hasNestedGroups?: boolean;
  nestedSummary?: string;
};

export type PreviewModifierGroup = {
  name?: string;
  description?: string;
  minimum?: number;
  maximum?: number;
  individualMaximum?: number;
  individualMinimum?: number;
  modifiers: PreviewModifier[];
  uuid?: string;
  key: string;
  groupKey: string;
};

export type GetSortedChildren = <T>(args: {
  itemKey?: string;
  itemType: string;
  childType: string;
  menu: PreviewMenu;
}) => T[];

// Builds the flat list of modifier groups that should currently render for an
// item: its own top-level groups, plus — for any modifier the user has
// already selected — that modifier's own nested groups, recursively. A
// modifier with no nested groups simply contributes nothing extra, so this
// naturally supports any depth of nesting without a separate "has children"
// check.
export const buildVisibleModifierGroups = ({
  itemKey,
  itemType,
  menu,
  getSortedChildren,
  formModifiers = {},
}: {
  itemKey?: string;
  itemType: string;
  menu: PreviewMenu;
  getSortedChildren: GetSortedChildren;
  formModifiers?: Record<string, ModifierGroupFormValue | undefined>;
}): PreviewModifierGroup[] =>
  getSortedChildren<ModifierGroupEntry>({
    itemKey,
    itemType,
    childType: 'modifierGroups',
    menu,
  }).flatMap(modifierGroup => {
    const {
      name,
      description,
      minimum,
      maximum,
      individualMaximum,
      individualMinimum,
      uuid,
      key,
      groupKey,
    } = modifierGroup;

    const groupModifiers: PreviewModifier[] = getSortedChildren<ModifierEntry>({
      itemKey: key,
      itemType: 'modifierGroups',
      childType: 'modifiers',
      menu,
    }).map(
      ({
        name: modifierName,
        description: modifierDescription,
        price,
        uuid: modifierUuid,
        parentUuidId,
        key: modifierKey,
        paused,
      }) => ({
        name: modifierName as string | undefined,
        description: modifierDescription as string | undefined,
        price: (price as { price?: number } | undefined)?.price,
        uuid: modifierUuid,
        parentUuidId,
        key: modifierKey,
        paused: paused as boolean | undefined,
      }),
    );

    const currentGroup: PreviewModifierGroup = {
      name: name as string | undefined,
      description: description as string | undefined,
      minimum: minimum as number | undefined,
      maximum: maximum as number | undefined,
      individualMaximum: individualMaximum as number | undefined,
      individualMinimum: individualMinimum as number | undefined,
      modifiers: groupModifiers,
      uuid,
      key,
      groupKey,
    };

    const groupValue = formModifiers[groupKey];
    const nestedGroups = groupModifiers.flatMap(modifier => {
      if (!isModifierSelected({ groupValue, modifierKey: modifier.key })) return [];
      return buildVisibleModifierGroups({
        itemKey: modifier.key,
        itemType: 'modifiers',
        menu,
        getSortedChildren,
        formModifiers,
      });
    });

    return [currentGroup, ...nestedGroups];
  });

export const modifierGroupBadgeText = (
  { minimum, maximum }: { minimum?: number; maximum?: number },
  t: TFunction<'modifierGroup'>,
): string => {
  if (!minimum) {
    return maximum
      ? t('ModifierGroup.badgeOptionalCapped', { maximum })
      : t('ModifierGroup.badgeOptional');
  }

  if (maximum && minimum !== maximum) {
    return t('ModifierGroup.badgeRequiredRange', { minimum, maximum });
  }

  if (maximum && minimum === maximum && minimum > 1) {
    return t('ModifierGroup.badgeRequiredChoose', { minimum });
  }

  if (!maximum) {
    return t('ModifierGroup.badgeAtLeast', { minimum });
  }

  // Only remaining case: minimum === maximum === 1 (every other minimum===maximum
  // combination is handled by the `minimum > 1` branch above).
  return t('ModifierGroup.badgeRequired');
};

export const filterClosedHours = (
  hours: Array<{ startTime: string; endTime: string }> = [],
): Array<{ startTime: string; endTime: string }> =>
  hours.filter(hourSet => hourSet.startTime !== hourSet.endTime && hourSet.endTime !== MIDNIGHT);

// given 2 times (format: "05:30") return negative, 0, or positive number
export const compareTimes = (time1: string, time2: string): number =>
  parseInt(time1.replace(':', ''), 10) - parseInt(time2.replace(':', ''), 10);

export const hasRollOver = (startTime: string, endTime: string): boolean =>
  compareTimes(startTime, endTime) > 0;
